import { useState, useEffect, useCallback } from 'react';
import Router, { useRouter } from 'next/router';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import usePaths from 'hooks/use-paths';
import { isBrowser } from 'utils/misc-utils';
import useErnie from 'shared/hooks/use-ernie';
import useTranslation from 'hooks/use-translation';

import { useNearestDispensaryLocation } from './use-nearest-dispensary-location';

export function useLocations() {
  const [address, setAddress] = useState(null);
  const { dispensary } = useDispensary();
  const { t } = useTranslation();
  const showErnie = useErnie();

  const { href, route } = usePaths();
  const router = useRouter();
  const searchByDeliveryArea = router.query?.searchMethod === 'deliveryArea';

  const { nearestDispensaryLocation, getNearestDispensaryLocation, error, loading } = useNearestDispensaryLocation(
    searchByDeliveryArea
  );

  const handleChangeAddress = useCallback(
    (location) => {
      if (location && dispensary) {
        setAddress(location);
        getNearestDispensaryLocation({
          variables: {
            dispensaryFilter: {
              type: `Dispensary`,
              chain: dispensary.chain,
              nearLat: location.lat,
              nearLng: location.lng,
              destinationTaxState: location.state,
              destinationTaxZipcode: location.zipcode,
              distance: 1000.0,
              deliversToAddress: searchByDeliveryArea,
              activeOnly: true,
              includeStealthMode: true,
              sortBy: `distance`,
            },
          },
        });
      }
    },
    [dispensary, getNearestDispensaryLocation, searchByDeliveryArea]
  );

  const handleClearAddress = () => setAddress(null);

  const hasErrors = (error || (dispensary && !dispensary?.chain)) && isBrowser();

  useEffect(() => {
    if (hasErrors) {
      Router.replace(route, href);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, hasErrors, showErnie, t]); // ignoring href && route here.

  useEffect(() => {
    if (searchByDeliveryArea && address && nearestDispensaryLocation) {
      Router.replace(`/embedded-menu/${nearestDispensaryLocation.cName}`);
    }
  }, [address, nearestDispensaryLocation, searchByDeliveryArea]);

  return {
    nearestDispensaryLocation,
    handleChangeAddress,
    address,
    handleClearAddress,
    searchByDeliveryArea,
    hasErrors,
    loading,
  };
}
